import {
  Box,
  Card,
  CardContent,
  Chip,
  FormControl,
  Grid,
  IconButton,
  InputAdornment,
  InputLabel,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TableSortLabel,
  TextField,
  Typography,
} from "@mui/material";
import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type MouseEvent,
  type ReactNode,
} from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import DevicesOtherOutlinedIcon from "@mui/icons-material/DevicesOtherOutlined";
import CloudDoneOutlinedIcon from "@mui/icons-material/CloudDoneOutlined";
import CloudOffOutlinedIcon from "@mui/icons-material/CloudOffOutlined";
import WarningAmberOutlinedIcon from "@mui/icons-material/WarningAmberOutlined";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import SearchIcon from "@mui/icons-material/Search";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import LaunchOutlinedIcon from "@mui/icons-material/LaunchOutlined";
import ContentCopyOutlinedIcon from "@mui/icons-material/ContentCopyOutlined";
import { PageContainer, PageHeader } from "../components/PageHeader";
import { KpiCard } from "../components/KpiCard";
import { TableSkeleton } from "../components/TableSkeleton";
import DeviceDetailDialog from "../components/DeviceDetailDialog";
import CatalogueHealthPanel from "../components/distribution/CatalogueHealthPanel";
import { PermissionGate } from "../auth/access";
import { tokens } from "../theme";
import { foyerApi } from "../http/foyer";
import { useScope } from "../context/ScopeContext";
import { platformLabel } from "../utils/devicePresentation";
import { openScreenPreview } from "../utils/preview";
import { useFormat } from "../i18n/format";
import { errorText } from "../utils/apiError";
import type {
  DevicePlatform,
  FleetAnalytics,
  FleetDevice,
  FleetHistory,
  FleetSummary,
} from "@shared/types/foyer";

/* -------------------------------------------------------------------------- */
/* Building blocks                                                             */
/* -------------------------------------------------------------------------- */

function SectionCard({
  title,
  action,
  children,
}: {
  title: string;
  action?: ReactNode;
  children: ReactNode;
}) {
  return (
    <Card sx={{ height: "100%" }}>
      <CardContent sx={{ p: 3 }}>
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          sx={{ mb: 2.5 }}
        >
          <Typography variant="h6">{title}</Typography>
          {action}
        </Stack>
        {children}
      </CardContent>
    </Card>
  );
}

/* ---- Stacked area chart (illustrative) ----------------------------------- */
function StackedAreaChart({
  labels,
  series,
}: {
  labels: string[];
  series: { label: string; color: string; data: number[] }[];
}) {
  const { t } = useTranslation("analytics");
  const W = 640;
  const H = 240;
  if (labels.length === 0) return <Box sx={{ height: H }} />;
  const padX = 8;
  const padTop = 12;
  const padBottom = 28;
  const innerW = W - padX * 2;
  const innerH = H - padTop - padBottom;
  const n = labels.length;
  const totals = labels.map((_, i) =>
    series.reduce((s, ser) => s + ser.data[i], 0)
  );
  const max = Math.max(...totals) * 1.1;
  const x = (i: number) => padX + (innerW * i) / (n - 1);
  const y = (v: number) => padTop + innerH - (v / max) * innerH;

  const cum = labels.map(() => 0);
  const bands = series.map((ser) => {
    const lower = cum.map((c) => c);
    ser.data.forEach((v, i) => (cum[i] += v));
    const upper = cum.map((c) => c);
    const top = upper.map((v, i) => `${x(i)} ${y(v)}`).join(" L ");
    const bottom = lower
      .map((_, i) => `${x(n - 1 - i)} ${y(lower[n - 1 - i])}`)
      .join(" L ");
    return {
      color: ser.color,
      path: `M ${top} L ${bottom} Z`,
      line: upper
        .map((v, i) => `${i === 0 ? "M" : "L"} ${x(i)} ${y(v)}`)
        .join(" "),
    };
  });
  const gridLines = [0, 0.5, 1];

  return (
    <Box>
      <Box sx={{ width: "100%" }}>
        <svg
          viewBox={`0 0 ${W} ${H}`}
          width="100%"
          height={H}
          preserveAspectRatio="none"
          role="img"
          aria-label={t("analyticsPage.chartAriaLabel")}
        >
          {gridLines.map((g) => (
            <line
              key={g}
              x1={padX}
              x2={W - padX}
              y1={padTop + innerH * g}
              y2={padTop + innerH * g}
              style={{ stroke: tokens.divider }}
              strokeWidth={1}
              vectorEffect="non-scaling-stroke"
            />
          ))}
          {bands.map((b, i) => (
            <g key={i}>
              <path d={b.path} style={{ fill: b.color, fillOpacity: 0.22 }} />
              <path
                d={b.line}
                fill="none"
                style={{ stroke: b.color }}
                strokeWidth={2.5}
                strokeLinecap="round"
                vectorEffect="non-scaling-stroke"
              />
            </g>
          ))}
        </svg>
      </Box>
      <Stack
        direction="row"
        justifyContent="space-between"
        sx={{ px: 0.5, mt: 0.5 }}
      >
        {labels.map((l) => (
          <Typography key={l} variant="caption" sx={{ typography: "micro" }}>
            {l}
          </Typography>
        ))}
      </Stack>
      <Stack direction="row" spacing={2} sx={{ mt: 1.5 }}>
        {series.map((s) => (
          <Stack
            key={s.label}
            direction="row"
            spacing={0.75}
            alignItems="center"
          >
            <Box
              sx={{
                width: 10,
                height: 10,
                borderRadius: "50%",
                bgcolor: s.color,
              }}
            />
            <Typography variant="caption">{s.label}</Typography>
          </Stack>
        ))}
      </Stack>
    </Box>
  );
}

/* ---- Donut (status breakdown) -------------------------------------------- */
function Donut({
  segments,
  centerValue,
  centerLabel,
}: {
  segments: { label: string; value: number; color: string }[];
  centerValue: string;
  centerLabel: string;
}) {
  const fmt = useFormat();
  const total = segments.reduce((s, x) => s + x.value, 0) || 1;
  const R = 56;
  const C = 2 * Math.PI * R;
  let offset = 0;
  return (
    <Stack direction="row" spacing={3} alignItems="center">
      <Box sx={{ position: "relative", width: 140, height: 140 }}>
        <svg width="140" height="140" viewBox="0 0 140 140">
          <circle
            cx="70"
            cy="70"
            r={R}
            fill="none"
            style={{ stroke: tokens.rowAlt }}
            strokeWidth="16"
          />
          {segments.map((s) => {
            const frac = s.value / total;
            const dash = `${frac * C} ${C - frac * C}`;
            const el = (
              <circle
                key={s.label}
                cx="70"
                cy="70"
                r={R}
                fill="none"
                style={{ stroke: s.color }}
                strokeWidth="16"
                strokeDasharray={dash}
                strokeDashoffset={-offset * C}
                transform="rotate(-90 70 70)"
                strokeLinecap="butt"
              />
            );
            offset += frac;
            return el;
          })}
        </svg>
        <Box
          sx={{
            position: "absolute",
            inset: 0,
            display: "grid",
            placeItems: "center",
            textAlign: "center",
          }}
        >
          <Box>
            <Typography
              sx={{
                typography: "headlineLg",
                fontWeight: "fontWeightBold",
                lineHeight: 1,
              }}
            >
              {centerValue}
            </Typography>
            <Typography variant="caption" sx={{ typography: "micro" }}>
              {centerLabel}
            </Typography>
          </Box>
        </Box>
      </Box>
      <Stack spacing={1.25} sx={{ flex: 1, minWidth: 0 }}>
        {segments.map((s) => (
          <Stack key={s.label} direction="row" alignItems="center" spacing={1}>
            <Box
              sx={{
                width: 10,
                height: 10,
                borderRadius: "50%",
                bgcolor: s.color,
                flexShrink: 0,
              }}
            />
            <Typography variant="body2" sx={{ flex: 1 }} noWrap>
              {s.label}
            </Typography>
            <Typography
              variant="body2"
              sx={{ fontWeight: "fontWeightSemibold" }}
            >
              {fmt.number(s.value)}
            </Typography>
          </Stack>
        ))}
      </Stack>
    </Stack>
  );
}

/* ---- Horizontal bar list ------------------------------------------------- */
function BarList({ items }: { items: { name: string; units: number }[] }) {
  const { t } = useTranslation("analytics");
  const max = Math.max(...items.map((i) => i.units), 1);
  if (items.length === 0) {
    return (
      <Typography variant="body2" color="text.secondary">
        {t("analyticsPage.noDevices")}
      </Typography>
    );
  }
  return (
    <Stack spacing={2}>
      {items.map((it) => (
        <Box key={it.name}>
          <Stack
            direction="row"
            justifyContent="space-between"
            sx={{ mb: 0.5 }}
          >
            <Typography
              variant="body2"
              noWrap
              sx={{ fontWeight: "fontWeightMedium", mr: 1 }}
            >
              {it.name}
            </Typography>
            <Typography variant="caption" sx={{ flexShrink: 0 }}>
              {t("analyticsPage.unitCount", { count: it.units })}
            </Typography>
          </Stack>
          <Box
            sx={{
              height: 8,
              borderRadius: 99,
              bgcolor: tokens.rowAlt,
              overflow: "hidden",
            }}
          >
            <Box
              sx={{
                width: `${(it.units / max) * 100}%`,
                height: "100%",
                borderRadius: 99,
                background: `linear-gradient(90deg, ${tokens.brand}, ${tokens.brandStrong})`,
              }}
            />
          </Box>
        </Box>
      ))}
    </Stack>
  );
}

/* ---- Heatmap (online rate by hour) --------------------------------------- */
function Heatmap({
  days,
  hours,
  grid,
}: {
  days: string[];
  hours: string[];
  grid: number[][];
}) {
  const { t } = useTranslation("analytics");
  const fmt = useFormat();
  return (
    <Box>
      <Box sx={{ display: "flex", gap: 1 }}>
        <Stack justifyContent="space-between" sx={{ py: 0.25 }}>
          {days.map((d) => (
            <Typography
              key={d}
              variant="caption"
              sx={{ typography: "micro", lineHeight: "18px" }}
            >
              {d}
            </Typography>
          ))}
        </Stack>
        <Box sx={{ flex: 1 }}>
          <Stack spacing={0.5}>
            {grid.map((row, ri) => (
              <Stack key={ri} direction="row" spacing={0.5}>
                {row.map((v, ci) => (
                  <Box
                    key={ci}
                    title={t("analyticsPage.heatmapCell", {
                      day: days[ri],
                      hour: hours[ci],
                      pct: fmt.number(v, {
                        style: "percent",
                        maximumFractionDigits: 0,
                      }),
                    })}
                    sx={{
                      flex: 1,
                      height: 18,
                      borderRadius: 0.75,
                      bgcolor: tokens.brand,
                      opacity: 0.1 + v * 0.9,
                    }}
                  />
                ))}
              </Stack>
            ))}
          </Stack>
          <Stack
            direction="row"
            justifyContent="space-between"
            sx={{ mt: 0.5 }}
          >
            {hours.map((h) => (
              <Typography
                key={h}
                variant="caption"
                sx={{ typography: "micro" }}
              >
                {h}
              </Typography>
            ))}
          </Stack>
        </Box>
      </Box>
    </Box>
  );
}

/* ---- Fleet health -------------------------------------------------------- */
type Alert = {
  icon: ReactNode;
  severity: "error" | "warning" | "success" | "info";
  title: string;
  sub: string;
  /** When set, the row applies this status filter to the device table below. */
  filter?: string;
};

const SEVERITY_COLOR: Record<string, string> = {
  error: tokens.error,
  warning: tokens.warning,
  success: tokens.success,
  info: tokens.brand,
};
const SEVERITY_TINT: Record<string, string> = {
  error: tokens.errorTint,
  warning: tokens.warningTint,
  success: tokens.successTint,
  info: tokens.brandTint,
};

function FleetHealth({
  alerts,
  onFilter,
}: {
  alerts: Alert[];
  onFilter: (status: string) => void;
}) {
  return (
    <Stack spacing={2}>
      {alerts.map((a) => {
        const clickable = Boolean(a.filter);
        return (
          <Stack
            key={a.title}
            direction="row"
            spacing={1.5}
            alignItems="center"
            onClick={clickable ? () => onFilter(a.filter!) : undefined}
            role={clickable ? "button" : undefined}
            tabIndex={clickable ? 0 : undefined}
            onKeyDown={
              clickable
                ? (e) => {
                    if (e.key === "Enter" || e.key === " ") onFilter(a.filter!);
                  }
                : undefined
            }
            sx={
              clickable
                ? {
                    cursor: "pointer",
                    borderRadius: 2,
                    mx: -1,
                    px: 1,
                    py: 0.5,
                    transition: "background-color 120ms",
                    "&:hover": { bgcolor: tokens.rowAlt },
                  }
                : undefined
            }
          >
            <Box
              sx={{
                width: 40,
                height: 40,
                borderRadius: 2,
                flexShrink: 0,
                display: "grid",
                placeItems: "center",
                bgcolor: SEVERITY_TINT[a.severity],
                color: SEVERITY_COLOR[a.severity],
              }}
            >
              {a.icon}
            </Box>
            <Box sx={{ minWidth: 0, flex: 1 }}>
              <Typography
                variant="body2"
                sx={{ fontWeight: "fontWeightSemibold" }}
                noWrap
              >
                {a.title}
              </Typography>
              <Typography variant="caption" sx={{ typography: "micro" }} noWrap>
                {a.sub}
              </Typography>
            </Box>
            {clickable && (
              <ChevronRightIcon
                sx={{ color: tokens.inkMuted, flexShrink: 0 }}
                fontSize="small"
              />
            )}
          </Stack>
        );
      })}
    </Stack>
  );
}

/** Online/offline status as a tinted pill with a leading status dot. */
function StatusPill({ online }: { online: boolean }) {
  const { t } = useTranslation("analytics");
  const color = online ? tokens.success : tokens.inkMuted;
  const bg = online ? tokens.successTint : tokens.rowAlt;
  return (
    <Box
      component="span"
      sx={{
        display: "inline-flex",
        alignItems: "center",
        gap: 0.75,
        px: 1,
        py: 0.25,
        borderRadius: 99,
        bgcolor: bg,
        color,
        typography: "meta",
        fontWeight: "fontWeightSemibold",
      }}
    >
      <Box
        component="span"
        sx={{ width: 7, height: 7, borderRadius: "50%", bgcolor: color }}
      />
      {online ? t("analyticsPage.online") : t("analyticsPage.offline")}
    </Box>
  );
}

const EMPTY_SUMMARY: FleetSummary = {
  total: 0,
  online: 0,
  offline: 0,
  byPlatform: {},
};

const SORT_FIELDS = [
  "premise",
  "room",
  "model",
  "platform",
  "firmware",
  "status",
  "lastSeen",
  "app",
  "streaming",
] as const;
type SortField = (typeof SORT_FIELDS)[number];
type SortDir = "asc" | "desc";

/** Comparable value for a device under a given sort column. */
function sortValue(d: FleetDevice, field: SortField): string | number {
  switch (field) {
    case "premise":
      return d.premiseName.toLowerCase();
    case "room":
      return (d.roomId ?? "").toLowerCase();
    case "model":
      return (d.live?.model ?? d.model ?? "").toLowerCase();
    case "platform":
      return d.platform ?? "";
    case "firmware":
      return (d.live?.firmware ?? "").toLowerCase();
    case "status":
      return d.online ? 1 : 0;
    case "lastSeen":
      return d.lastSeenAt ? Date.parse(d.lastSeenAt) : 0;
    case "app":
      return (d.live?.appVersion ?? "").toLowerCase();
    case "streaming":
      return (d.live?.streamingAppVersion ?? "").toLowerCase();
  }
}

/* -------------------------------------------------------------------------- */
/* Device table (paginated)                                                    */
/* -------------------------------------------------------------------------- */

function DeviceTable({
  initialStatus,
  innerRef,
  fleetScope,
}: {
  initialStatus: string;
  innerRef: React.Ref<HTMLDivElement>;
  fleetScope: {
    companyId?: string;
    premiseId?: string;
    premiseIds?: string[];
  };
}) {
  const [devices, setDevices] = useState<FleetDevice[]>([]);
  const [summary, setSummary] = useState<FleetSummary>(EMPTY_SUMMARY);
  const [error, setError] = useState<string | null>(null);
  const [query, setQuery] = useState("");
  const [platform, setPlatform] = useState("");
  const [status, setStatus] = useState(initialStatus);
  const [detail, setDetail] = useState<FleetDevice | null>(null);
  const [sortField, setSortField] = useState<SortField>("premise");
  const [sortDir, setSortDir] = useState<SortDir>("asc");
  const [menuAnchor, setMenuAnchor] = useState<HTMLElement | null>(null);
  const [menuDevice, setMenuDevice] = useState<FleetDevice | null>(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const { t } = useTranslation("analytics");
  const fmt = useFormat();

  // A deep-link / health-alert click updates the requested status filter.
  useEffect(() => {
    setStatus(initialStatus);
  }, [initialStatus]);

  function openMenu(e: MouseEvent<HTMLElement>, d: FleetDevice) {
    e.stopPropagation();
    setMenuAnchor(e.currentTarget);
    setMenuDevice(d);
  }
  function closeMenu() {
    setMenuAnchor(null);
    setMenuDevice(null);
  }

  function toggleSort(field: SortField) {
    if (field === sortField) {
      setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    } else {
      setSortField(field);
      setSortDir("asc");
    }
  }

  const refresh = useCallback(async () => {
    try {
      const res = await foyerApi.fleetDevices({
        ...fleetScope,
        q: query || undefined,
        platform: platform || undefined,
        status: status || undefined,
      });
      setDevices(res.devices);
      setSummary(res.summary);
      setError(null);
    } catch (err) {
      setError(errorText(err, t));
    }
  }, [query, status, platform, fleetScope, t]);

  useEffect(() => {
    refresh();
    const timer = window.setInterval(refresh, 5_000);
    return () => window.clearInterval(timer);
  }, [refresh]);

  // Reset to the first page whenever the result set changes shape.
  useEffect(() => {
    setPage(0);
  }, [query, status, platform, sortField, sortDir]);

  const sorted = useMemo(() => {
    const arr = [...devices];
    arr.sort((a, b) => {
      const va = sortValue(a, sortField);
      const vb = sortValue(b, sortField);
      const cmp =
        typeof va === "string"
          ? va.localeCompare(String(vb))
          : Number(va) - Number(vb);
      return sortDir === "asc" ? cmp : -cmp;
    });
    return arr;
  }, [devices, sortField, sortDir]);

  const paged = useMemo(
    () => sorted.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage),
    [sorted, page, rowsPerPage]
  );

  return (
    <Card ref={innerRef} sx={{ scrollMarginTop: 16 }}>
      <CardContent sx={{ p: 3 }}>
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          sx={{ mb: 2.5, flexWrap: "wrap", gap: 1 }}
        >
          <Typography variant="h6">
            {t("analyticsPage.table.title")}
            <Typography
              component="span"
              variant="body2"
              color="text.secondary"
              sx={{ ml: 1 }}
            >
              {t("analyticsPage.table.counts", {
                total: fmt.number(summary.total),
                online: fmt.number(summary.online),
              })}
            </Typography>
          </Typography>
        </Stack>

        {/* Filters */}
        <Box
          sx={{
            display: "flex",
            flexWrap: "wrap",
            gap: 2,
            alignItems: "center",
            mb: 2,
          }}
        >
          <TextField
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder={t("analyticsPage.table.searchPlaceholder")}
            size="small"
            sx={{ flex: 1, minWidth: 240, maxWidth: 360 }}
            inputProps={{ "data-testid": "fleet-search" }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              ),
            }}
          />
          <FormControl size="small" sx={{ minWidth: 150 }}>
            <InputLabel>{t("analyticsPage.table.platform")}</InputLabel>
            <Select
              label={t("analyticsPage.table.platform")}
              value={platform}
              onChange={(e) => setPlatform(e.target.value)}
            >
              <MenuItem value="">
                {t("analyticsPage.table.allPlatforms")}
              </MenuItem>
              {Object.keys(summary.byPlatform)
                .filter((p) => p !== "unknown")
                .sort()
                .map((p) => (
                  <MenuItem key={p} value={p}>
                    {platformLabel(p as DevicePlatform)}
                  </MenuItem>
                ))}
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ minWidth: 130 }}>
            <InputLabel>{t("analyticsPage.table.status")}</InputLabel>
            <Select
              label={t("analyticsPage.table.status")}
              value={status}
              onChange={(e) => setStatus(e.target.value)}
            >
              <MenuItem value="">{t("analyticsPage.table.anyStatus")}</MenuItem>
              <MenuItem value="online">{t("analyticsPage.online")}</MenuItem>
              <MenuItem value="offline">{t("analyticsPage.offline")}</MenuItem>
              <MenuItem value="never">
                {t("analyticsPage.table.neverReported")}
              </MenuItem>
            </Select>
          </FormControl>
        </Box>

        {error && (
          <Typography color="error" variant="body2" sx={{ mb: 2 }}>
            {error}
          </Typography>
        )}

        {devices.length === 0 ? (
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              gap: 1,
              py: 6,
              color: "text.secondary",
            }}
          >
            <DevicesOtherOutlinedIcon sx={{ fontSize: 40, opacity: 0.4 }} />
            <Typography variant="body2">
              {t("analyticsPage.table.noMatch")}
            </Typography>
          </Box>
        ) : (
          <>
            <TableContainer>
              <Table size="small" data-testid="fleet-table">
                <TableHead>
                  <TableRow>
                    {SORT_FIELDS.map((field) => (
                      <TableCell
                        key={field}
                        sortDirection={sortField === field ? sortDir : false}
                      >
                        <TableSortLabel
                          active={sortField === field}
                          direction={sortField === field ? sortDir : "asc"}
                          onClick={() => toggleSort(field)}
                        >
                          {t(`analyticsPage.table.columns.${field}`)}
                        </TableSortLabel>
                      </TableCell>
                    ))}
                    <TableCell padding="checkbox" />
                  </TableRow>
                </TableHead>
                <TableBody>
                  {paged.map((d) => (
                    <TableRow
                      key={d.id}
                      hover
                      tabIndex={0}
                      role="button"
                      aria-label={t("analyticsPage.table.rowAriaLabel", {
                        premise: d.premiseName,
                        room: d.roomId ?? "",
                      })}
                      sx={{ cursor: "pointer" }}
                      onClick={() => setDetail(d)}
                      onKeyDown={(e) => {
                        if (e.key === "Enter" || e.key === " ") {
                          e.preventDefault();
                          setDetail(d);
                        }
                      }}
                      data-testid={`fleet-row-${d.id}`}
                    >
                      <TableCell>
                        <Typography
                          variant="body2"
                          fontWeight="fontWeightSemibold"
                        >
                          {d.premiseName}
                        </Typography>
                      </TableCell>
                      <TableCell>{d.roomId ?? "-"}</TableCell>
                      <TableCell>{d.live?.model ?? d.model}</TableCell>
                      <TableCell>
                        {d.platform ? (
                          <Chip
                            size="small"
                            variant="outlined"
                            label={platformLabel(d.platform)}
                          />
                        ) : (
                          <Typography variant="body2" color="text.secondary">
                            -
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        <Typography
                          variant="caption"
                          sx={{
                            fontFamily: "fontFamilyMono",
                            color: "text.secondary",
                          }}
                        >
                          {d.live?.firmware ?? "-"}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <StatusPill online={d.online} />
                      </TableCell>
                      <TableCell>
                        <Typography variant="caption" color="text.secondary">
                          {d.lastSeenAt
                            ? fmt.relative(d.lastSeenAt)
                            : t("analyticsPage.never")}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography
                          variant="caption"
                          sx={{
                            fontFamily: "fontFamilyMono",
                            color: "text.secondary",
                          }}
                        >
                          {d.live?.appVersion ?? "-"}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography
                          variant="caption"
                          sx={{
                            fontFamily: "fontFamilyMono",
                            color: "text.secondary",
                          }}
                        >
                          {d.live?.streamingAppVersion ?? "-"}
                        </Typography>
                      </TableCell>
                      <TableCell padding="checkbox">
                        <IconButton
                          size="small"
                          aria-label={t("analyticsPage.table.actionsFor", {
                            premise: d.premiseName,
                          })}
                          onClick={(e) => openMenu(e, d)}
                          data-testid={`fleet-row-menu-${d.id}`}
                        >
                          <MoreVertIcon fontSize="small" />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
            <TablePagination
              component="div"
              count={sorted.length}
              page={page}
              onPageChange={(_, p) => setPage(p)}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={(e) => {
                setRowsPerPage(parseInt(e.target.value, 10));
                setPage(0);
              }}
              rowsPerPageOptions={[10, 25, 50, 100]}
            />
          </>
        )}
      </CardContent>

      <Menu
        anchorEl={menuAnchor}
        open={Boolean(menuAnchor)}
        onClose={closeMenu}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <MenuItem
          onClick={() => {
            if (menuDevice) setDetail(menuDevice);
            closeMenu();
          }}
        >
          <ListItemIcon>
            <InfoOutlinedIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>{t("analyticsPage.table.viewDetails")}</ListItemText>
        </MenuItem>
        <MenuItem
          onClick={() => {
            if (menuDevice)
              openScreenPreview({
                premiseId: menuDevice.premiseId,
                screenId: menuDevice.id,
                role: menuDevice.role,
              });
            closeMenu();
          }}
        >
          <ListItemIcon>
            <LaunchOutlinedIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>
            {menuDevice?.role === "signage"
              ? t("analyticsPage.table.openSignagePreview")
              : t("analyticsPage.table.openPreview")}
          </ListItemText>
        </MenuItem>
        <MenuItem
          disabled={!menuDevice?.serial}
          onClick={() => {
            if (menuDevice?.serial)
              navigator.clipboard?.writeText(menuDevice.serial);
            closeMenu();
          }}
        >
          <ListItemIcon>
            <ContentCopyOutlinedIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>{t("analyticsPage.table.copySerial")}</ListItemText>
        </MenuItem>
      </Menu>

      <DeviceDetailDialog
        open={detail !== null}
        premiseId={detail?.premiseId ?? ""}
        screen={detail}
        onClose={() => setDetail(null)}
        onChanged={refresh}
        onOpenPreview={(s, role) => {
          openScreenPreview({
            premiseId: (s as FleetDevice).premiseId,
            screenId: s.id,
            role,
          });
          setDetail(null);
        }}
      />
    </Card>
  );
}

/* -------------------------------------------------------------------------- */
/* Page                                                                        */
/* -------------------------------------------------------------------------- */

type Range = "day" | "week" | "month";
const RANGES: Range[] = ["day", "week", "month"];

function FilterSelect({
  value,
  onChange,
  options,
}: {
  value: string;
  onChange: (v: string) => void;
  options: { value: string; label: string }[];
}) {
  return (
    <Select
      size="small"
      value={value}
      onChange={(e) => onChange(e.target.value)}
      sx={{ minWidth: 150, bgcolor: "background.paper" }}
    >
      {options.map((o) => (
        <MenuItem key={o.value} value={o.value}>
          {o.label}
        </MenuItem>
      ))}
    </Select>
  );
}

export default function AnalyticsPage() {
  const navigate = useNavigate();
  const { scope, label: scopeLabel, premisesInScope } = useScope();
  // A product filter is a set of places, not a device attribute - resolve it
  // here so the KPIs, the history and the table all count the same fleet.
  const fleetScope = useMemo(
    () => ({
      companyId: scope.companyId || undefined,
      premiseId: scope.premiseId || undefined,
      premiseIds:
        !scope.premiseId && scope.product
          ? premisesInScope.map((p) => p.premiseId)
          : undefined,
    }),
    [scope.companyId, scope.premiseId, scope.product, premisesInScope]
  );
  const [searchParams] = useSearchParams();
  const [data, setData] = useState<FleetAnalytics | null>(null);
  const [history, setHistory] = useState<FleetHistory | null>(null);
  const [range, setRange] = useState<Range>("week");
  const tableRef = useRef<HTMLDivElement>(null);
  const { t } = useTranslation(["analytics", "common"]);
  const fmt = useFormat();
  const pct = (n: number) =>
    fmt.number(n / 100, { style: "percent", maximumFractionDigits: 0 });
  // The status filter for the device table, deep-linkable via ?status= and
  // set by the Fleet-health cards.
  const statusFilter = searchParams.get("status") ?? "";

  // Scroll the table into view when arriving with a status deep-link.
  useEffect(() => {
    if (statusFilter && tableRef.current) {
      tableRef.current.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  }, [statusFilter]);

  const filterDevices = useCallback(
    (status: string) => {
      navigate(status ? `/analytics?status=${status}` : "/analytics");
      tableRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
    },
    [navigate]
  );

  useEffect(() => {
    let cancelled = false;
    async function load() {
      try {
        const a = await foyerApi.fleetAnalytics(fleetScope);
        if (!cancelled) setData(a);
      } catch {
        // Leave the last good data on transient errors.
      }
    }
    load();
    const t = window.setInterval(load, 30_000);
    return () => {
      cancelled = true;
      window.clearInterval(t);
    };
  }, [fleetScope]);

  useEffect(() => {
    let cancelled = false;
    async function load() {
      try {
        const h = await foyerApi.fleetHistory(range, fleetScope);
        if (!cancelled) setHistory(h);
      } catch {
        // Leave the last good history on transient errors.
      }
    }
    load();
    const t = window.setInterval(load, 60_000);
    return () => {
      cancelled = true;
      window.clearInterval(t);
    };
  }, [range, fleetScope]);

  const statusSegments = useMemo(() => {
    if (!data) return [];
    const stale = Math.max(0, data.offline - data.neverPaired);
    return [
      {
        label: t("analyticsPage.online"),
        value: data.online,
        color: tokens.brand,
      },
      { label: t("analyticsPage.offline"), value: stale, color: tokens.error },
      {
        label: t("analyticsPage.neverPaired"),
        value: data.neverPaired,
        color: tokens.inkFaint,
      },
    ];
  }, [data, t]);

  const platformItems = useMemo(
    () =>
      (data?.byPlatform ?? []).map((p) => ({
        name: platformLabel(p.name as DevicePlatform),
        units: p.units,
      })),
    [data]
  );

  const alerts = useMemo<Alert[]>(() => {
    if (!data) return [];
    const list: Alert[] = [];
    if (data.offlineOver24h > 0) {
      list.push({
        icon: <ErrorOutlineIcon />,
        severity: "error",
        title: t("analyticsPage.alerts.offlineOver24h", {
          count: data.offlineOver24h,
        }),
        sub: t("analyticsPage.alerts.offlineOver24hSub"),
        filter: "offline",
      });
    }
    if (data.neverPaired > 0) {
      list.push({
        icon: <WarningAmberOutlinedIcon />,
        severity: "warning",
        title: t("analyticsPage.alerts.neverReported", {
          count: data.neverPaired,
        }),
        sub: t("analyticsPage.alerts.neverReportedSub"),
        filter: "never",
      });
    }
    for (const a of data.attention) {
      list.push({
        icon: <CloudOffOutlinedIcon />,
        severity: "error",
        title: t("analyticsPage.alerts.deviceOffline", {
          device: a.roomId ?? t("analyticsPage.alerts.aDevice"),
          hours: fmt.number(a.hoursOffline),
        }),
        sub: a.premiseName,
      });
    }
    if (list.length === 0) {
      list.push({
        icon: <CheckCircleOutlineIcon />,
        severity: "success",
        title: t("analyticsPage.alerts.allNormal"),
        sub: t("analyticsPage.alerts.allNormalSub", {
          online: fmt.number(data.online),
          installed: fmt.number(data.installed),
        }),
      });
    }
    return list.slice(0, 6);
  }, [data, t, fmt]);

  if (!data) {
    return (
      <PageContainer>
        <PageHeader
          eyebrow={t("analyticsPage.eyebrow")}
          title={t("analyticsPage.title")}
          subtitle={scopeLabel}
        />
        <Grid container spacing={2.5}>
          {[0, 1, 2, 3].map((i) => (
            <Grid item xs={12} sm={6} md={3} key={i}>
              <Card sx={{ p: 3 }}>
                <Box sx={{ height: 88 }} />
              </Card>
            </Grid>
          ))}
        </Grid>
        <TableSkeleton rows={4} showHeader={false} />
      </PageContainer>
    );
  }

  return (
    <PageContainer>
      <PageHeader
        eyebrow={t("analyticsPage.eyebrow")}
        title={t("analyticsPage.title")}
        subtitle={scopeLabel}
      />

      {/* KPI row - live snapshot */}
      <Grid container spacing={2.5}>
        <Grid item xs={12} sm={6} md={3}>
          <KpiCard
            label={t("analyticsPage.kpi.installed")}
            value={fmt.number(data.installed)}
            sub={t("analyticsPage.kpi.installedSub", {
              count: data.premiseCount,
            })}
            icon={<DevicesOtherOutlinedIcon />}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <KpiCard
            label={t("analyticsPage.kpi.onlineNow")}
            value={fmt.number(data.online)}
            sub={t("analyticsPage.kpi.onlineSub", { pct: pct(data.onlinePct) })}
            icon={<CloudDoneOutlinedIcon />}
            tint={tokens.successTint}
            accent={tokens.success}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <KpiCard
            label={t("analyticsPage.kpi.offlineNow")}
            value={fmt.number(data.offline)}
            sub={t("analyticsPage.kpi.offlineSub", {
              pct: pct(100 - data.onlinePct),
            })}
            icon={<CloudOffOutlinedIcon />}
            tint={data.offline > 0 ? tokens.errorTint : tokens.rowAlt}
            accent={data.offline > 0 ? tokens.error : tokens.inkMuted}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <KpiCard
            label={t("analyticsPage.kpi.needsAttention")}
            value={fmt.number(data.offlineOver24h)}
            sub={t("analyticsPage.kpi.needsAttentionSub")}
            icon={<WarningAmberOutlinedIcon />}
            tint={data.offlineOver24h > 0 ? tokens.warningTint : tokens.rowAlt}
            accent={data.offlineOver24h > 0 ? tokens.warning : tokens.inkMuted}
          />
        </Grid>
      </Grid>

      {/* Content health - what in the catalogue will not play or is missing
          metadata. High on the page: a list of things to fix is more
          actionable than the charts below it. */}
      <PermissionGate
        resource="distribution/titles"
        level="administrate"
        place="nenda"
      >
        <CatalogueHealthPanel />
      </PermissionGate>

      {/* Status over time + breakdown */}
      <Grid container spacing={2.5}>
        <Grid item xs={12} md={8}>
          <SectionCard
            title={t("analyticsPage.sections.statusOverTime")}
            action={
              <FilterSelect
                value={range}
                onChange={(v) => setRange(v as Range)}
                options={RANGES.map((r) => ({
                  value: r,
                  label: t(`common:labels.${r}`),
                }))}
              />
            }
          >
            <StackedAreaChart
              labels={history?.series.labels ?? []}
              series={[
                {
                  label: t("analyticsPage.online"),
                  color: tokens.brand,
                  data: history?.series.online ?? [],
                },
                {
                  label: t("analyticsPage.offline"),
                  color: tokens.error,
                  data: history?.series.offline ?? [],
                },
              ]}
            />
          </SectionCard>
        </Grid>
        <Grid item xs={12} md={4}>
          <SectionCard title={t("analyticsPage.sections.statusBreakdown")}>
            <Donut
              segments={statusSegments}
              centerValue={pct(data.onlinePct)}
              centerLabel={t("analyticsPage.donutCenter")}
            />
          </SectionCard>
        </Grid>
      </Grid>

      {/* Locations + platforms (live) */}
      <Grid container spacing={2.5}>
        <Grid item xs={12} md={6}>
          <SectionCard title={t("analyticsPage.sections.unitsPerLocation")}>
            <BarList items={data.byLocation} />
          </SectionCard>
        </Grid>
        <Grid item xs={12} md={6}>
          <SectionCard title={t("analyticsPage.sections.devicesByPlatform")}>
            <BarList items={platformItems} />
          </SectionCard>
        </Grid>
      </Grid>

      {/* Uptime heatmap + health */}
      <Grid container spacing={2.5}>
        <Grid item xs={12} md={8}>
          <SectionCard title={t("analyticsPage.sections.onlineRateByHour")}>
            <Heatmap
              days={history?.heatmap.days ?? []}
              hours={history?.heatmap.hours ?? []}
              grid={history?.heatmap.grid ?? []}
            />
          </SectionCard>
        </Grid>
        <Grid item xs={12} md={4}>
          <SectionCard title={t("analyticsPage.sections.healthSignals")}>
            <FleetHealth alerts={alerts} onFilter={filterDevices} />
          </SectionCard>
        </Grid>
      </Grid>

      {/* Full device list */}
      <DeviceTable
        initialStatus={statusFilter}
        innerRef={tableRef}
        fleetScope={fleetScope}
      />
    </PageContainer>
  );
}
